<template>
    <div class="card border-0 bg-white profile-settings-card rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
            <form v-on:submit="submit">
                <div class="row">
                    <!-- LANG -->
                    <div class="col-md-6" v-if="update">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25" v-if="locales.length > 1">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('feature.contents.lang') }}
                            </label>
                            <div class="d-flex gap-3 align-items-center">
                                <country-flag :country="lang" size="big" />
                                <select class="form-select shadow-none text-black p-15 pe-50 fs-14 fw-medium w-auto"
                                    v-model="lang" @change="changeLang()" required>
                                    <option v-for="locale in locales" :value="locale" :key="locale">{{
                                        $t(`feature.contents.$lang.${locale}`) }}</option>
                                </select>
                            </div>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.lang ?? false">
                                <p v-for="error in errors.lang" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- NAME -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.title.label') }}*
                            </label>
                            <input v-model="survey.title![lang]" class="form-control shadow-none text-black" required
                                :class="[{ 'is-invalid': submitted && errors?.title }]" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.title ?? false">
                                <p v-for="error in errors.title" :key="error" class="small text-danger lh-1 m-0 mt-1">
                                    {{ error }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- KEY -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.key.label') }}*
                                <small class="d-block text-body-tertiary fw-normal">
                                    {{ $t('feature.surveys.key_recommendations') }}
                                </small>
                            </label>
                            <input v-model="survey.key" class="form-control shadow-none text-black" required
                                :class="[{ 'is-invalid': submitted && errors?.key }]" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.key ?? false">
                                <p v-for="error in errors.key" :key="error" class="small text-danger lh-1 m-0 mt-1">
                                    {{ error }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- DESCRIPTION -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.description.label') }}
                            </label>
                            <textarea v-model="survey.description![lang]" class="form-control shadow-none text-black"
                                :class="[{ 'is-invalid': submitted && errors?.description }]" rows="6"></textarea>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.description ?? false">
                                <p v-for="error in errors.description" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">
                                    {{ error }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <!-- FOOTER TEXT -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.footer.label') }}
                            </label>
                            <textarea v-model="survey.footer_text![lang]" class="form-control shadow-none text-black"
                                :class="[{ 'is-invalid': submitted && errors?.footer_text }]" rows="6"></textarea>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.footer_text ?? false">
                                <p v-for="error in errors.footer_text" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">
                                    {{ error }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- FREQUENCY -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('feature.surveys.frequency') }}*
                            </label>
                            <div class="d-flex gap-3 align-items-center">
                                <select class="form-select shadow-none text-black p-15 pe-50 fs-14 fw-medium w-auto"
                                    v-model="survey.frequency" required>
                                    <option v-for="key in frequencies" :value="key" :key="key">
                                        {{ $t(`feature.surveys.$frequency.${key}`) }}</option>
                                </select>
                            </div>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.frequency ?? false">
                                <p v-for="error in errors.frequency" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">
                                    {{ error }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <!-- IS NOTIFIABLE -->
                    <div class="col-12">
                        <div class="form-check form-group form-switch mb-15 mb-sm-20 mb-md-25">
                            <input class="form-check-input shadow-none me-8" type="checkbox" role="switch"
                                v-model="survey.is_notifiable" id="is_notifiable" :true-value="1" :false-value="0">
                            <label class="form-check-label fw-medium position-relative top-1" for="is_notifiable">
                                {{ $t('feature.surveys.is_notifiable') }}
                            </label>
                        </div>
                    </div>
                    <!-- FREQUENCY DAYS -->
                    <div class="col-sm-6 col-xl-4" v-if="survey.is_notifiable">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25 w-auto">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('feature.surveys.default_frequency_days') }}*
                            </label>
                            <input v-model="survey.default_frequency_days" type="number"
                                class="form-control shadow-none text-black w-auto" :required="survey.is_notifiable"
                                :class="[{ 'is-invalid': submitted && errors?.default_frequency_days }]" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.default_frequency_days ?? false">
                                <p v-for="error in errors.default_frequency_days" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">
                                    {{ error }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <!-- FREQUENCY TIME -->
                    <div class="col-sm-6 col-xl-4" v-if="survey.is_notifiable">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25 w-auto">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('feature.surveys.default_notification_time') }}*
                            </label>
                            <input v-model="survey.default_notification_time" pattern="[0-9]{2}:[0-9]{2}" type="time"
                                :required="survey.is_notifiable" class="form-control shadow-none text-black w-auto"
                                :class="[{ 'is-invalid': submitted && errors?.default_notification_time }]" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.default_notification_time ?? false">
                                <p v-for="error in errors.default_notification_time" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">
                                    {{ error }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <!-- EXTRAS -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('feature.surveys.extra') }}
                                <span v-if="survey.extra !== undefined" class="text-success"><i
                                        class="ph ph-check-circle"></i></span>
                            </label>
                            <code>
                                    <textarea v-model="local_extra" class="form-control shadow-none text-black"
                                    :class="[{ 'is-invalid': submitted && errors?.extra }]" rows="6"></textarea>
                                </code>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.extra ?? false">
                                <p v-for="error in errors.extra" :key="error" class="small text-danger lh-1 m-0 mt-1">
                                    {{ error }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <!-- <div class="col-12">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25 w-auto">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('feature.surveys.extra') }}
                            </label>
                            <div class="row align-items-center" v-for="(extra, index) in extras" :key="index">
                                <div class="col-sm-6 col-md-4 mb-15">
                                    <input v-model="extras[index][0]" class="form-control shadow-none text-black" required :placeholder="$t('form.key.label')"/>
                                </div>
                                <div class="col-sm-6 col-md-4 mb-15">
                                    <input v-model="extras[index][1]" class="form-control shadow-none text-black" required :placeholder="$t('form.value.label')"/>
                                </div>
                                <div class="col-auto mb-30 mb-md-15 ms-auto ms-md-0">
                                    <button type="button" @click="extras.splice(index, 1)"
                                                class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium">
                                                <span class="position-relative">{{ $t('cta.delete') }}</span>
                                            </button>
                                </div>
                            </div>
                            <button class="default-btn outlined transition fw-medium fs-md-15 fs-lg-16"
                                    type="button" v-on:click="extras.push(['',undefined])">
                                    {{ $t('cta.add') }}
                            </button>
                            
                            <div class="form-messages mt-2 px-1" v-if="errors?.extra ?? false">
                                <p v-for="error in errors.extra" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">
                                    {{ error }}
                                </p>
                            </div>
                        </div>
                    </div> -->

                    <div class="col-12" v-if="loading">
                        <SpinnerLoading class="mb-25" />
                    </div>
                    <div class="col-md-12" v-else>
                        <div class="d-flex align-items-center justify-content-between gap-3 mb-25">
                            <!-- DELETE -->
                            <button v-if="update" type="button"
                                class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium" data-bs-toggle="modal"
                                data-bs-target="#confirmModal">
                                <i class="ph ph-trash lh-1 me-1 position-relative top-2"></i>
                                <span class="position-relative">{{ $t('cta.delete_survey') }}</span>
                            </button>
                            <span v-else></span>
                            <!-- SAVE -->
                            <div class="d-flex flex-wrap gap-3 justify-content-end ms-auto">
                                <DiscardButton @confirm="$router.push({ path: '/surveys' })" />
                                <button
                                    class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                                    type="submit" v-on:click="submitted = true">
                                    {{ $t('cta.save_survey') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- BLOCKS -->
                    <div class="accordion border-0" id="step-form-accordion" v-if="survey.steps">
                        <DraggableList :list="survey.steps" item-key="id" handle=".block-form .block-handle" sort="true"
                            @start="dragStart" @change="orderSteps">
                            <template #item="step">
                                <div class="accordion-item block-form border-0 rounded-0 border-0">
                                    <button
                                        class="accordion-button d-block w-100 text-start text-black fs-14 fs-md-15 fs-lg-16 fw-medium rounded-0 bg-white border-0 collapsed p-15 p-sm-20 p-sm-25 p-lg-30 pe-50"
                                        type="button" data-bs-toggle="collapse"
                                        :data-bs-target="`#step-tab-${step.element.id}`" aria-expanded="false"
                                        :aria-controls="`step-tab-${step.element.id}`">
                                        <div class="d-sm-flex align-items-center justify-content-between pe-3">
                                            <div class="d-flex gap-3 align-items-center">
                                                <div class="block-handle fw-bold text-muted">
                                                    {{ step.index + 1 }}
                                                </div>
                                                <h5 class="fw-medium text-black-emphasis mb-0 flex-grow-1">{{
                                                    step.element.title[lang] }}
                                                </h5>
                                            </div>
                                            <span class="text-muted text-uppercase small badge bg-light">
                                                {{ $t(`feature.surveys.$steps.$type.${step.element.type}`) }}
                                            </span>
                                        </div>
                                    </button>
                                    <div :id="`step-tab-${step.element.id}`"
                                        class="accordion-collapse collapse border-0"
                                        data-bs-parent="#step-form-accordion">
                                        <div class="border-0 mt-20 py-15 py-sm-20 py-sm-25 py-lg-30">
                                            <!-- BODY -->
                                            <!-- TODO: SurveyStepForm -->
                                            <StepForm :step="(step.element as SurveyStep)" :key="step.element.id"
                                                :update="true" :lang="lang"
                                                @update="updated => updateStep(step.index, updated)"
                                                @changeLang="(event) => changeLang(event)"
                                                @delete="deleteStep(step.index)" />
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </DraggableList>
                    </div>
                    <!--  -->

                    <!-- ADD -->
                    <div class="d-flex justify-content-between gap-3 mt-25 flex-wrap" v-if="update">
                        <p class="mb-8">{{ $t('feature.surveys.$steps.drag_reorder') }}</p>
                        <button
                            class="ms-auto default-btn outlined transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                            type="button" @click="addBlock">
                            {{ $t('feature.surveys.$steps.add') }}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
    <!-- ERROR MESSAGE -->
    <div class="row justify-content-center">
        <div class="col">
            <div class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0 mb-25"
                role="alert" id="errorMessage">
                <strong>{{ $t('error.something_went_wrong') }}</strong>
                <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
            </div>
        </div>
    </div>
    <ConfirmModal @confirm="deleteSurvey()" :title="$t('cta.$delete.title')" :message="$t('cta.$delete.message')" />
    <SurveyStepModal id="step_modal" :survey_id="survey.id!" @save="event => saveStep(event)" v-if="update" />
    <!-- <ConfirmModal @confirm="deleteCover()" :id="`deleteCover`" :title="$t('cta.$delete.title')"
        :message="$t('cta.$delete.message')" /> -->
</template>

<style lang="scss">
.accordion .accordion-item .accordion-button {
    --splash-danger-color: transparent;
    padding-top: 15px !important;
    padding-bottom: 15px !important;
    border-bottom: none !important;
    display: block;
}

.block-handle {
    cursor: grab;
    cursor: -moz-grab;
    cursor: -webkit-grab;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background-color: #e9ecef;
    box-shadow: 0px 4px 34px rgba(101, 96, 240, 0.1) !important;
}

.sortable-chosen {
    .accordion-button {
        outline: 1px solid !important;
        outline-color: #d1d5db !important;
    }

    .block-handle {
        pointer-events: all !important;
        cursor: grabbing !important;
        cursor: -moz-grabbing !important;
        cursor: -webkit-grabbing !important;
    }

    &.sortable-ghost {
        .block-handle {
            pointer-events: all !important;
            cursor: grabbing !important;
            cursor: -moz-grabbing !important;
            cursor: -webkit-grabbing !important;
        }

        .card-body {
            outline: none;
            box-shadow: none;
        }
    }
}
</style>

<script lang="ts">
import { defineComponent, ref } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { removeNullProperties } from "@/utils/methods/remove_null_properties";
import API from "@/services/api/api.service";
import { OrderField } from "@/utils/models/_model";
import { Survey, SurveyFrequency, SurveyStep } from "@/utils/models/survey.model";
import SurveyStepModal from "@/components/FK/Surveys/Admin/SurveyStepModal.vue";
import StepForm from "@/components/FK/Surveys/Admin/StepForm.vue";
import { Modal } from "bootstrap";
import DiscardButton from "@/components/Widgets/DiscardButton.vue";
import CountryFlag from 'vue-country-flag-next';
import config from "@/utils/const/config";

export default defineComponent({
    setup() {
        const coverPickerRef = ref();
        return {
            coverPickerRef,
        };
    },
    name: "SurveyForm",
    components: {
        ConfirmModal,
        SpinnerLoading,
        DiscardButton,
        SurveyStepModal,
        CountryFlag,
        StepForm
    },
    data: function () {
        const _survey = this.$route.meta.survey as Survey;
        return {
            survey: {
                ..._survey as Survey,
                title: _survey?.title ?? {},
                description: _survey?.description ?? {},
                footer_text: _survey?.footer_text ?? {},
            } as Survey,
            submitted: false,
            loading: false,
            errors: undefined as any,
            stepModal: null as Modal | null,
            originalSteps: undefined as SurveyStep[] | undefined,
            lang: (this.$route.query.lang ?? process.env.VUE_APP_LANG) as string,
            extras: Object.entries(_survey?.extra ?? {}) as string[][],
            local_extra: JSON.stringify(_survey?.extra) as string,
        }
    },
    watch: {
        extras: {
            deep: true,
            handler(next) {
                this.survey.extra = Object.fromEntries(next);
            }
        },
        'local_extra': function (next) {
            try {
                this.survey.extra = JSON.parse(next);
            } catch (error) {
                this.survey.extra = undefined;
            }
        }
    },
    mounted() {
        if (this.update) {
            this.stepModal = new Modal('#step_modal', { backdrop: 'static' })
        }
    },
    computed: {
        update(): boolean {
            return this.$route.meta.survey !== undefined && this.$route.meta.survey !== null;
        },
        locales(): string[] {
            return config.available_locales
        },
        frequencies(): SurveyFrequency {
            return Object(SurveyFrequency);
        }
    },
    methods: {
        submit(event): void {
            this.errors = undefined;
            event.preventDefault();
            console.log("CONTENT", this.survey);
            removeNullProperties<Survey>(this.survey);

            this.loading = true;
            if (this.update) {
                console.log('UPDATE');
                Promise.all([
                    API.updateSurvey({
                        ...this.survey,
                        title: this.survey.title?.[process.env.VUE_APP_LANG],
                        description: undefined,
                        footer_text: undefined,
                    }),
                    API.updateSurveyLocalization(this.survey, this.lang)
                ]).then((response) => {
                    // this.$router.push(`/content-category/${this.content?.content_category_id!}`);
                }).catch((error) => {
                    this.errors = error.response?.data?.errors;
                    this.showError();
                }).finally(() => {
                    this.loading = false;
                });
            } else {
                console.log('CREATE');
                API.createSurvey(this.survey).then((response) => {
                    this.$router.push(`/survey/${response.data.data.id}/edit`);
                }).catch((error) => {
                    this.errors = error.response?.data?.errors;
                    this.showError();
                }).finally(() => {
                    this.loading = false;
                });
            }
        },
        deleteSurvey(): void {
            this.loading = true;
            API.deleteSurvey(this.survey.id!).then((response) => {
                if (response.data.success === true) {
                    this.$router.push(`/surveys`);
                }
            }).catch((error) => {
                this.showError();
            }).finally(() => {
                console.log("DELETED_SURVEY_ID", this.survey.id!);
                this.loading = false;
            })
        },
        showError() {
            const error = document.getElementById('errorMessage');
            error?.classList.add('show');
            setTimeout(() => {
                this.hideError();
            }, 5000)
        },
        hideError() {
            const error = document.getElementById('errorMessage');
            error?.classList.remove('show');
        },
        addBlock() {
            this.stepModal?.show();
        },
        saveStep(step: SurveyStep) {
            this.survey.steps?.push(step);
            this.stepModal?.hide();
        },
        updateStep(index: number, step: SurveyStep) {
            this.survey.steps![index] = step;
            console.log(this.survey);
        },
        deleteStep(index: number) {
            this.survey.steps?.splice(index, 1);
        },
        dragStart() {
            this.originalSteps = [...this.survey.steps ?? []];
        },
        orderSteps(event, originalEvent) {
            console.log('event', event);
            console.log('original_event', originalEvent);
            console.log('blocks', this.survey.steps);

            let order: OrderField[] | undefined = this.survey.steps?.map((step, index) => ({ id: step.id!, order: index } as OrderField));
            if (order !== undefined) {
                API.orderSurveySteps({ step_id: this.survey.id!, step_order: order }).then((response) => {
                    if (response.data.success === true) {
                        this.originalSteps = [...this.survey.steps ?? []];
                    } else {
                        throw Error('ORDER_BLOCK_ERROR');
                    }
                }).catch((error) => {
                    this.survey.steps = [...this.originalSteps ?? []];
                    console.error(error);
                })
            }
        },
        changeLang(lang?: string) {
            console.log(lang);
            if (lang !== undefined) {
                this.lang = lang;
            }
        }
    },
});
</script>